import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { useSnackbar } from "notistack";

import api from "/imports/api/client";
import { usePreferencesForm, PreferencesFields } from "/imports/ui/components/preferences";

function PreferencesEditForm({ slug, initial, onCancel, onSaved }) {
  const { enqueueSnackbar } = useSnackbar();
  const form = usePreferencesForm(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // Pure opt-out: the client is subscribed unless they explicitly opted out
  // ("unknown" — never touched this toggle — still means subscribed). The
  // toggle shows that true current state, so flipping it is a deliberate
  // opt-in/out. Sent only when changed — unrelated preference saves must not
  // re-stamp consent.
  const initialWaOptIn = initial?.whatsappConsent !== "opted_out";
  const [waOptIn, setWaOptIn] = useState(initialWaOptIn);

  const handleSave = async () => {
    const { payload, error: validationError } = form.buildPayload();
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSubmitting(true);
    try {
      const consentChange = waOptIn !== initialWaOptIn ? { whatsappOptIn: waOptIn } : {};
      await api.updateUserPreferences(slug, { ...payload, ...consentChange });
      onSaved();
    } catch (err) {
      enqueueSnackbar(err.message || "Failed to save preferences", { variant: "error" });
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" mb={2}>
        Tell us what you like so your club can tailor recommendations to you.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <PreferencesFields form={form} />
      <FormControlLabel
        sx={{ mt: 2 }}
        control={<Switch checked={waOptIn} onChange={(e) => setWaOptIn(e.target.checked)} />}
        label={
          <Box>
            <Typography variant="body2" color="textSecondary">
              Club offers &amp; updates
            </Typography>
            <Typography variant="caption" color="textSecondary">
              WhatsApp messages and app notifications
            </Typography>
          </Box>
        }
      />
      <Box display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
        <Button disabled={submitting} onClick={onCancel} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button variant="contained" disabled={submitting} onClick={handleSave} sx={{ textTransform: "none", color: "common.white" }}>
          {submitting ? <CircularProgress size={20} /> : "Save changes"}
        </Button>
      </Box>
    </Box>
  );
}

export default PreferencesEditForm;
